// Pure voice-strategy logic — the fallback ladder, kept framework-free so it's
// unit-testable without a browser. The React glue lives in useVoiceInput.ts.
//
// Ladder (docs/05 §Voice):
//   1. Web Speech API (webkitSpeechRecognition) — live transcription, no upload.
//   2. MediaRecorder → POST /transcribe (Workers AI Whisper) — server transcript.
//   3. Nothing usable, or /transcribe returned 503 → hide the mic.
// Voice NEVER auto-sends; both strategies only fill the composer for review.

export type VoiceStrategy = 'speech' | 'record' | 'unavailable';

export interface VoiceCaps {
  speechRecognition: boolean;
  mediaRecorder: boolean;
}

/** Detect capabilities from a window-like object (injectable for tests). */
export function detectVoiceCaps(win: unknown): VoiceCaps {
  const w = (win ?? {}) as Record<string, unknown>;
  const speechRecognition = 'webkitSpeechRecognition' in w || 'SpeechRecognition' in w;
  const mediaRecorder =
    typeof w.MediaRecorder !== 'undefined' &&
    typeof w.navigator === 'object' &&
    w.navigator !== null &&
    typeof (w.navigator as { mediaDevices?: unknown }).mediaDevices === 'object';
  return { speechRecognition, mediaRecorder };
}

/**
 * Resolve the strategy to use. `transcribeDisabled` latches true once the
 * server has told us Whisper is unavailable (503) — from then on the record
 * fallback is dead and the mic hides entirely.
 */
export function effectiveStrategy(caps: VoiceCaps, transcribeDisabled: boolean): VoiceStrategy {
  if (caps.speechRecognition) return 'speech';
  if (caps.mediaRecorder && !transcribeDisabled) return 'record';
  return 'unavailable';
}

/** Grab the SpeechRecognition constructor across vendor prefixes. */
export function getSpeechRecognitionCtor(
  win: unknown,
): (new () => SpeechRecognitionLike) | null {
  const w = (win ?? {}) as Record<string, unknown>;
  const ctor = w.webkitSpeechRecognition ?? w.SpeechRecognition;
  return typeof ctor === 'function' ? (ctor as new () => SpeechRecognitionLike) : null;
}

// Minimal structural type for the Web Speech API — the lib DOM types aren't
// guaranteed present and we only touch this surface.
export interface SpeechRecognitionLike {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: ((event: SpeechResultLike) => void) | null;
  onerror: ((event: { error?: string }) => void) | null;
  onend: (() => void) | null;
}

export interface SpeechResultLike {
  resultIndex: number;
  results: ArrayLike<ArrayLike<{ transcript: string }> & { isFinal: boolean }>;
}

/** Flatten a SpeechRecognition result event into a single transcript string. */
export function transcriptFromEvent(event: SpeechResultLike): string {
  let out = '';
  for (let i = 0; i < event.results.length; i++) {
    const res = event.results[i];
    if (res) out += res[0]?.transcript ?? '';
  }
  return out.trim();
}
